import { oc } from "@orpc/contract";
import { z } from "zod";
import { Cents } from "./money";

/**
 * Bundles' admin API (CONTEXT.md, #60 and #64): the components of a product of kind `bundle` and
 * its stock mode. The product itself (name, price, photos) is the product editor's; this is the
 * editor's "Onderdelen" card. Every write answers the whole `BundleEditor`, which the card shows.
 *
 * Rules: a component is a live variant of a `standard` product, so a bundle never holds a bundle
 * (nor itself); a variant is in a bundle at most once. The bundle's price is its own (the spec: a
 * kit may be cheaper than its parts); `componentsConsumerPrice` shows what the parts cost apart.
 *
 * Errors come back as `{ _tag, message }` with an HTTP status; the tags are named on each procedure.
 * An invalid body or query answers 400.
 */

/** `composed`: availability follows from the components. `prepacked`: own stock. */
export const BundleStockMode = z.enum(["composed", "prepacked"]);
export type BundleStockMode = z.infer<typeof BundleStockMode>;

/** How many of a component one bundle holds. */
export const ComponentQuantity = z.number().int().min(1).max(999);

/** The most components a bundle may have. */
export const MAX_BUNDLE_COMPONENTS = 50;

/** One component of a bundle. */
export const BundleComponent = z.object({
	variantId: z.string(),
	productId: z.string(),
	productName: z.string(),
	/** The value names in option order (`XXL / Blauw`); the product's name without options. */
	variantName: z.string(),
	sku: z.string(),
	quantity: ComponentQuantity,
	/** The component's own consumer price, incl VAT, per piece. */
	consumerPrice: Cents,
	/** False when the variant or its product is inactive: not for sale on its own. */
	active: z.boolean(),
	/** How many of the component can still be sold (`Stock.availability`). */
	available: z.number().int(),
});
export type BundleComponent = z.infer<typeof BundleComponent>;

/** Everything the "Onderdelen" card shows. */
export const BundleEditor = z.object({
	productId: z.string(),
	stockMode: BundleStockMode,
	/** In the order they were added. */
	components: z.array(BundleComponent),
	/** The components' consumer prices times their quantities, summed: the parts bought apart. */
	componentsConsumerPrice: Cents,
	/**
	 * How many bundles can still be sold. `composed`: the smallest, over the components, of
	 * floor(available / quantity), 0 without components. `prepacked`: the bundle's own stock.
	 */
	available: z.number().int(),
});
export type BundleEditor = z.infer<typeof BundleEditor>;

/** A variant that can become a component: live, of a `standard` product. */
export const ComponentCandidate = z.object({
	variantId: z.string(),
	productId: z.string(),
	productName: z.string(),
	variantName: z.string(),
	sku: z.string(),
	consumerPrice: Cents,
	active: z.boolean(),
	/** Already a component of this bundle. */
	inBundle: z.boolean(),
});
export type ComponentCandidate = z.infer<typeof ComponentCandidate>;

export const ComponentCandidateQuery = z.object({
	/** Part of the product's name, of the variant's value names or of its SKU, any case. */
	search: z.string().trim().max(200).default(""),
});
export type ComponentCandidateQuery = z.infer<typeof ComponentCandidateQuery>;

export const AddComponentBody = z.object({
	variantId: z.string().min(1),
	quantity: ComponentQuantity.default(1),
});
export type AddComponentBody = z.infer<typeof AddComponentBody>;

export const ComponentQuantityBody = z.object({ quantity: ComponentQuantity });
export type ComponentQuantityBody = z.infer<typeof ComponentQuantityBody>;

export const StockModeBody = z.object({ stockMode: BundleStockMode });
export type StockModeBody = z.infer<typeof StockModeBody>;

const BundlePath = z.object({ productId: z.string().min(1) });
const ComponentPath = BundlePath.extend({ variantId: z.string().min(1) });

// ---------------------------------------------------------------------------------------------
// Procedures (admin only: every one needs a signed-in employee)

/** Every write answers `ProductArchived` (409) while the bundle is archived: restore it first (spec, A2). */
export const BundleContract = {
	/** Errors: `BundleNotFound` (404): no product with this id, or it is not a bundle. */
	get: oc.route({ method: "GET", path: "/admin/bundles/{productId}" }).input(BundlePath).output(BundleEditor),
	/** Up to 20 variants for the card's search, by product name. Errors: `BundleNotFound` (404). */
	candidates: oc
		.route({ method: "GET", path: "/admin/bundles/{productId}/candidates" })
		.input(BundlePath.extend(ComponentCandidateQuery.shape))
		.output(z.array(ComponentCandidate)),
	/**
	 * Errors: `BundleNotFound` (404), `ComponentVariantNotFound` (404): no live variant with this id,
	 * `NestedBundle` (409): the variant belongs to a bundle, `ComponentExists` (409),
	 * `TooManyComponents` (409).
	 */
	addComponent: oc
		.route({ method: "POST", path: "/admin/bundles/{productId}/components" })
		.input(BundlePath.extend(AddComponentBody.shape))
		.output(BundleEditor),
	/** Errors: `BundleNotFound` (404), `ComponentNotFound` (404). */
	updateComponent: oc
		.route({ method: "PATCH", path: "/admin/bundles/{productId}/components/{variantId}" })
		.input(ComponentPath.extend(ComponentQuantityBody.shape))
		.output(BundleEditor),
	/** Errors: `BundleNotFound` (404), `ComponentNotFound` (404). */
	removeComponent: oc
		.route({ method: "DELETE", path: "/admin/bundles/{productId}/components/{variantId}" })
		.input(ComponentPath)
		.output(BundleEditor),
	/**
	 * Switches between `composed` and `prepacked`; only while no reservation holds stock for the
	 * bundle, and to composed only once the bundle's own variant has no stock left. Errors:
	 * `BundleNotFound` (404), `OpenReservations` (409), `BundleHasStock` (409).
	 */
	setStockMode: oc
		.route({ method: "PUT", path: "/admin/bundles/{productId}/stock-mode" })
		.input(BundlePath.extend(StockModeBody.shape))
		.output(BundleEditor),
};
